import React, { ReactElement, useEffect, useMemo } from "react";
import { Banner } from "@companion-core/shared/app/Components/Blocks/BaseComponent/Banner";
import { ButtonList } from "@companion-core/shared/app/Components/Blocks/BaseComponent/ButtonList";
import { CardMember } from "@companion-core/shared/app/Components/Blocks/BaseComponent/CardMember";
import { Detail } from "@companion-core/shared/app/Components/Blocks/BaseComponent/Detail";
import { List } from "@companion-core/shared/app/Components/Blocks/BaseComponent/List";
import { PictureList } from "@companion-core/shared/app/Components/Blocks/BaseComponent/PictureList";
import { PostList } from "@companion-core/shared/app/Components/Blocks/BaseComponent/PostList";
import { PostListJourney } from "@companion-core/shared/app/Components/Blocks/BaseComponent/PostListJourney";
import { Selection } from "@companion-core/shared/app/Components/Blocks/BaseComponent/Selection";
import { Tab } from "@companion-core/shared/app/Components/Blocks/BaseComponent/Tab";
import { VisualBanner } from "@companion-core/shared/app/Components/Blocks/BaseComponent/VisualBanner";
import { useBlockItems } from "@companion-core/shared/app/Queries";
import { BlockProps } from "@companion-core/shared/app/Interfaces/blocks";
import { DynamicContentProps } from "@companion-core/shared/app/Components/Blocks/DynamicContent/DynamicContent.model";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import { APIBlockItem } from "@companion-core/shared/app/Interfaces/API";
import { isBlockItemsContainAQuery } from "@companion-core/shared/app/Utils/componentHelper";
import { BlockSkeleton } from "@companion-core/shared/app/Components/Skeletons/BlockSkeleton/BlockSkeleton";
import { BlockSkeletonType } from "@companion-core/shared/app/Components/Skeletons/BlockSkeleton/blockSkeleton.model";
import { usePersonalizeField } from "@companion-core/shared/app/Hooks/personalization";
import {
  isJourneyBlock,
  isOnboardingEnabled,
} from "@companion-core/shared/app/Components/Blocks/Layouts/PostListJourneyLayout/postListJourneyLayout.utils";
import { Geolocation } from "@companion-core/shared/app/Components/Blocks/BaseComponent/Geolocation";
import { PushNotification } from "@companion-core/shared/app/Components/Blocks/BaseComponent/PushNotification";
import { Universe } from "../BaseComponent/Universe";

type SupportedBlockType = Exclude<BlockSkeletonType, "post-list-journey">;

const Blocks: Record<
  SupportedBlockType,
  ({ simpleBlockItems, queryBlockItems, contentProps }: BlockProps) => ReactElement | undefined
> = {
  "button-list": ButtonList,
  "card-member": CardMember,
  "detail": Detail,
  "header": Banner,
  "header-visual": VisualBanner,
  "list": List,
  "picture-list": PictureList,
  "post-list": PostList,
  "selection": Selection,
  "tab": Tab,
  "geolocation": Geolocation,
  "push-notification": PushNotification,
  "universe": Universe,
  "newsletter": PushNotification,
};

const isSupportedBlockType = (blockType: string): blockType is SupportedBlockType =>
  blockType in Blocks;

export const DynamicContent = ({
  className = "",
  type,
  contentProps,
  stepperGoToNext,
  stepperGoToPrevious,
}: DynamicContentProps) => {
  const isJourney = isOnboardingEnabled() && isJourneyBlock(type, contentProps.displayType);
  const Block = isJourney ? PostListJourney : isSupportedBlockType(type) ? Blocks[type] : undefined;
  const { personalizeObject } = usePersonalizeField();
  const { setBlockContent, isLoaded } = useDynamicPageStore();
  const { isPending, data: blockItems } = useBlockItems({
    id: contentProps.id,
    lang: contentProps.lang,
    enabled: Boolean(Block),
  });
  const personalizedBlockItems = useMemo(() => {
    if (!blockItems) return [];

    return blockItems.map((blockItem) => ({
      ...blockItem,
      components: {
        ...blockItem.components,
        data: blockItem.components.data.map((component) => personalizeObject(component)),
      },
    })) as APIBlockItem[];
  }, [blockItems, personalizeObject]);
  const queryBlockItems = useMemo(
    () => personalizedBlockItems.filter((blockItem) => blockItem.type === "query"),
    [personalizedBlockItems]
  );
  const simpleBlockItems = useMemo(
    () => personalizedBlockItems.filter((blockItem) => blockItem.type === "simple"),
    [personalizedBlockItems]
  );
  const isBlockLoaded = useMemo(() => {
    if (personalizedBlockItems.length && isBlockItemsContainAQuery(personalizedBlockItems)) {
      return true;
    }

    if (personalizedBlockItems.length > 0) {
      return true;
    }

    return isLoaded;
  }, [personalizedBlockItems, isLoaded]);

  useEffect(() => {
    if (personalizedBlockItems.length) {
      setBlockContent(contentProps.slug, personalizedBlockItems);
    }
  }, [contentProps.slug, personalizedBlockItems, setBlockContent]);

  const skeletonType: BlockSkeletonType = isJourney
    ? "post-list-journey"
    : (type as SupportedBlockType);

  if (!Block) {
    return null;
  }

  return !isPending && isBlockLoaded ? (
    <Block
      className={className}
      simpleBlockItems={simpleBlockItems}
      queryBlockItems={queryBlockItems}
      contentProps={contentProps}
      stepperGoToNext={stepperGoToNext}
      stepperGoToPrevious={stepperGoToPrevious}
    />
  ) : (
    <BlockSkeleton type={skeletonType} />
  );
};
